import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useDispatch } from 'react-redux';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import type { ICampaignReferralEarningsIfFriendHired } from '../../types/global/campaignReferralEarnings.types';
import type { IBuzzbridgeNetworkInvite } from '../../types/global/buzzbridgeLoyalty.types';
import { toggleAuthModal } from '../../redux/authentication/action';
import { getLoyaltyInvite } from '../../redux/buzzbridgeLoyalty/actions';
import { useBuzzbridgeLoyaltyInvite } from './Loyalty/useBuzzbridgeLoyaltyInvite';
import { useDispatchPromise } from '../../utils/AppUtils';
import { showToastMessage } from '../../utils/AppUtils';
import { copyReferralLink } from './Loyalty/loyaltyReferralShare';
import {
    buildCampaignReferralShareUrl,
    consumePendingCampaignReferralCopy,
    formatCampaignReferralEarnAmount,
    resolveCampaignReferralCopyDisplay,
    setPendingCampaignReferralCopy,
} from '../../utils/campaignReferralShare';
import { MixPanel, MIXPANEL_EVENT } from '../../analytics/Mixpanel';

interface CampaignReferralEarnSidebarProps {
    campaignId: string;
    earnings: ICampaignReferralEarningsIfFriendHired;
    isSignedIn: boolean;
    variant?: 'sidebar' | 'mobileDock';
}

export function CampaignReferralEarnSidebar({
    campaignId,
    earnings,
    isSignedIn,
    variant = 'sidebar',
}: CampaignReferralEarnSidebarProps) {
    const dispatch = useDispatch();
    const dispatchPromise = useDispatchPromise();
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const { invite } = useBuzzbridgeLoyaltyInvite();
    const [copied, setCopied] = useState(false);

    const referralCopy = resolveCampaignReferralCopyDisplay(earnings);
    const earnAmount = formatCampaignReferralEarnAmount(earnings);
    const isVisible = earnings.enabled && Boolean(referralCopy || earnAmount);

    const copyShareLink = useCallback(async () => {
        if (!isSignedIn) return false;
        let linkInvite: IBuzzbridgeNetworkInvite | null | undefined = invite;
        if (!linkInvite?.referral_link) {
            try {
                linkInvite = (await dispatchPromise(getLoyaltyInvite())) as IBuzzbridgeNetworkInvite;
            } catch {
                showToastMessage('Could not load your referral link', 'error');
                return false;
            }
        }
        if (!linkInvite?.referral_link) {
            showToastMessage('Referral link unavailable', 'error');
            return false;
        }
        const url = buildCampaignReferralShareUrl(linkInvite, campaignId);
        const ok = await copyReferralLink(url);
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
        return ok;
    }, [campaignId, dispatchPromise, invite, isSignedIn]);

    useEffect(() => {
        if (!isVisible || !isSignedIn || !consumePendingCampaignReferralCopy(campaignId)) return;
        void copyShareLink();
    }, [isVisible, isSignedIn, campaignId, copyShareLink]);

    if (!isVisible) return null;

    const isMobileDock = variant === 'mobileDock';

    const handleLoggedOutClick = () => {
        MixPanel.track(MIXPANEL_EVENT.COPY_REFERRAL_LINK_CLICK, {
            buttonName: 'SIGN IN AND COPY LINK',
            buttonAction: 'COPY_REFERRAL_LINK',
            campaignId,
            signedIn: false,
            source: variant === 'mobileDock' ? 'refer_earn_mobile' : 'refer_earn_sidebar',
        });
        setPendingCampaignReferralCopy(campaignId);
        dispatch(toggleAuthModal(true));
    };

    const handleLoggedInClick = () => {
        MixPanel.track(MIXPANEL_EVENT.COPY_REFERRAL_LINK_CLICK, {
            buttonName: 'COPY REFERRAL LINK',
            buttonAction: 'COPY_REFERRAL_LINK',
            campaignId,
            signedIn: true,
            source: variant === 'mobileDock' ? 'refer_earn_mobile' : 'refer_earn_sidebar',
        });
        void copyShareLink();
    };

    return (
        <Box
            sx={{
                p: isMobileDock ? 0 : 3,
                bgcolor: isMobileDock ? 'transparent' : t.card_grey,
                borderRadius: isMobileDock ? 0 : 3,
                border: isMobileDock ? 'none' : `1px solid rgba(255, 201, 101, 0.12)`,
                width: '100%',
            }}>
            <Typography
                sx={{
                    fontSize: isMobileDock ? 9 : 10,
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    color: t.highlight,
                    mb: isMobileDock ? 1 : 1.5,
                }}>
                Refer & earn
            </Typography>
            <Typography
                sx={{
                    fontSize: isMobileDock ? 13 : 14,
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    color: t.tag_white,
                    lineHeight: 1.45,
                    mb: referralCopy?.perReferral ? (isMobileDock ? 0.75 : 1) : isMobileDock ? 1.5 : 2,
                }}>
                {referralCopy?.headline ? (
                    <>
                        <Box component="span" sx={{ color: t.highlight, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            {referralCopy.headline}
                        </Box>
                        {referralCopy.detail ? ` ${referralCopy.detail}` : null}
                    </>
                ) : referralCopy?.short ? (
                    referralCopy.short
                ) : (
                    <>
                        Earn{' '}
                        <Box component="span" sx={{ color: t.highlight, fontFamily: FONT_ENUM.MAIN_BOLD }}>
                            {earnAmount}
                        </Box>{' '}
                        when your referral completes this job
                    </>
                )}
            </Typography>
            {referralCopy?.perReferral ? (
                <Typography
                    sx={{
                        fontSize: isMobileDock ? 11 : 12,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        color: t.subtle_white,
                        mb: isMobileDock ? 1.5 : 2,
                    }}>
                    {referralCopy.perReferral}
                </Typography>
            ) : null}
            <Button
                fullWidth
                onClick={isSignedIn ? handleLoggedInClick : handleLoggedOutClick}
                startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                sx={{
                    py: isMobileDock ? 1.1 : 1.25,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: isMobileDock ? 12 : 13,
                    color: isSignedIn && copied ? t.true_white : '#392700',
                    bgcolor: isSignedIn && copied ? 'rgba(0,253,193,0.15)' : t.highlight,
                    border: isSignedIn && copied ? `1px solid rgba(0,253,193,0.35)` : 'none',
                    '&:hover': {
                        bgcolor: isSignedIn && copied ? 'rgba(0,253,193,0.2)' : t.true_highlight,
                    },
                }}>
                {isSignedIn ? (copied ? 'Link copied' : 'Copy referral link') : 'Sign in & copy link'}
            </Button>
        </Box>
    );
}
